/**
 * GA4 event helpers — all 50 events from the tracking spec.
 * Each function wraps gaEvent() with the exact parameter shape defined per event.
 * Import only what you need in each screen.
 */
import { gaEvent } from "../analytics";

// ─── QR SCAN ENTRY & LANGUAGE ────────────────────────────────────────────────

/** #1 — User arrives on microsite via QR scan on pack */
export const trackQrScanLand = (params = {}) =>
  gaEvent("qr_scan_land", params);

/** #2 — Every screen load */
export const trackPageView = ({ page_title, screen_name }) =>
  gaEvent("page_view", { page_title, screen_name });

/** #3 — User picks Tamil or English */
export const trackLanguageSelect = ({ language }) =>
  gaEvent("language_select", { language });

/** Custom — User selects their state in the sign-up form */
export const trackStateSelect = ({ state, screen_name = "signup" }) =>
  gaEvent("state_select", { state, screen_name });

// ─── REGISTRATION FUNNEL ─────────────────────────────────────────────────────

/** #4 — Sign up form loads */
export const trackSignupStart = ({ source, utm_medium, utm_campaign }) =>
  gaEvent("signup_start", { source, utm_medium, utm_campaign });

/** #5 — User taps into any form field */
export const trackSignupFieldInteract = ({ field_name }) =>
  gaEvent("signup_field_interact", { field_name });

/** #6 — Validation error appears on a field */
export const trackSignupValidationError = ({ field_name, error_type }) =>
  gaEvent("signup_validation_error", { field_name, error_type });

/** #7 — User taps T&C checkbox */
export const trackConsentCheckboxTap = ({ already_checked }) =>
  gaEvent("consent_checkbox_tap", { already_checked });

/** #8 — User taps "Next" (form submission attempt) */
export const trackSignupSubmit = ({ state, has_consent }) =>
  gaEvent("signup_submit", { state, has_consent });

/** #9 — OTP SMS dispatched */
export const trackOtpRequested = ({ delivery_status = "sent" } = {}) =>
  gaEvent("otp_requested", { delivery_status });

/** #10 — User submits OTP code */
export const trackOtpEntered = ({ is_valid }) =>
  gaEvent("otp_entered", { is_valid });

/** #11 — User taps "Resend Code" */
export const trackOtpResend = ({ attempt_count }) =>
  gaEvent("otp_resend", { attempt_count });

/** #12 — Timer reaches 00:00 */
export const trackOtpTimeout = ({ time_elapsed }) =>
  gaEvent("otp_timeout", { time_elapsed });

/** #13 — Account created successfully */
export const trackSignupComplete = ({ state, language, reg_flow_version = "v1" }) =>
  gaEvent("signup_complete", { state, language, reg_flow_version });

// ─── POST-REGISTRATION SURVEY ────────────────────────────────────────────────

/** #14 — Personalisation popup appears */
export const trackProfileSurveyShown = ({ time_since_registration }) =>
  gaEvent("profile_survey_shown", { time_since_registration });

/** #15 — User taps "Confirm!" on survey */
export const trackProfileSurveySubmit = ({ family_size, num_children, child_age_group }) =>
  gaEvent("profile_survey_submit", { family_size, num_children, child_age_group });

/** #16 — User dismisses survey popup */
export const trackProfileSurveyDismiss = ({ time_on_popup }) =>
  gaEvent("profile_survey_dismiss", { time_on_popup });

// ─── LOGIN — RETURNING ────────────────────────────────────────────────────────

/** #17 — Login screen loads */
export const trackLoginStart = ({ days_since_last_visit }) =>
  gaEvent("login_start", { days_since_last_visit });

/** #18 — User taps "Enter" on login form */
export const trackLoginSubmit = ({ phone_masked }) =>
  gaEvent("login_submit", { phone_masked });

/** #19 — "Wrong OTP" displayed on login */
export const trackLoginOtpWrong = ({ attempt_count }) =>
  gaEvent("login_otp_wrong", { attempt_count });

/** #20 — Session created after successful login */
export const trackLoginSuccess = ({ session_id, days_since_registration, kurals_in_progress }) =>
  gaEvent("login_success", { session_id, days_since_registration, kurals_in_progress });

// ─── PURCHASE PROOF — MACHINE CODE ───────────────────────────────────────────

/** #21 — Code entry screen loads */
export const trackMachineCodeScreenView = ({ entry_point }) =>
  gaEvent("machine_code_screen_view", { entry_point });

/** #22 — Camera activates for code scan */
export const trackMachineCodeCameraOpen = ({ device_model, os_version }) =>
  gaEvent("machine_code_camera_open", { device_model, os_version });

/** #23 — Camera reads 16-digit code */
export const trackMachineCodeScanSuccess = ({ scan_duration_ms }) =>
  gaEvent("machine_code_scan_success", { scan_duration_ms });

/** #24 — User types the code instead of scanning */
export const trackMachineCodeManualEntry = ({ characters_corrected = 0 } = {}) =>
  gaEvent("machine_code_manual_entry", { characters_corrected });

/** #25 — Code accepted — valid purchase */
export const trackMachineCodeValidated = ({ validation_result, sku_detected }) =>
  gaEvent("machine_code_validated", { validation_result, sku_detected });

/** #26 — "Couldn't verify" error shown */
export const trackMachineCodeRejected = ({ rejection_reason, attempt_count }) =>
  gaEvent("machine_code_rejected", { rejection_reason, attempt_count });

// ─── GAMEPLAY — BISCUIT SCAN ─────────────────────────────────────────────────

/** #27 — How to Play tutorial shown */
export const trackHowToPlayView = ({ auto_shown_vs_tapped }) =>
  gaEvent("how_to_play_view", { auto_shown_vs_tapped });

/** #28 — Camera opens for biscuit word */
export const trackBiscuitScanStart = ({ kural_id, word_position } = {}) =>
  gaEvent("biscuit_scan_start", { kural_id, word_position });

/** #29 — Word detected on biscuit */
export const trackBiscuitScanSuccess = ({ kural_id, word_detected, word_position, scan_duration_ms }) =>
  gaEvent("biscuit_scan_success", { kural_id, word_detected, word_position, scan_duration_ms });

/** #30 — "Scan failed. Try again." */
export const trackBiscuitScanFail = ({ kural_id, word_position, attempt_count }) =>
  gaEvent("biscuit_scan_fail", { kural_id, word_position, attempt_count });

/** #31 — Cashback popup appears after first scan */
export const trackCashbackRewardShown = ({ kural_id, reward_amount }) =>
  gaEvent("cashback_reward_shown", { kural_id, reward_amount });

/** #32 — User taps "Claim Reward" */
export const trackCashbackClaimTap = ({ reward_amount }) =>
  gaEvent("cashback_claim_tap", { reward_amount });

// ─── GAMEPLAY — KURAL WORDS ──────────────────────────────────────────────────

/** #33 — User submits a word (typed or scanned) */
export const trackKuralWordEnter = ({ kural_id, word, position, method }) =>
  gaEvent("kural_word_enter", { kural_id, word, position, method });

/** #34 — Word accepted */
export const trackKuralAnswerCorrect = ({ kural_id, word_position, time_to_enter_ms }) =>
  gaEvent("kural_answer_correct", { kural_id, word_position, time_to_enter_ms });

/** #35 — "Wrong answer. Try again!" */
export const trackKuralAnswerWrong = ({ kural_id, word_attempted, correct_word, attempt_count }) =>
  gaEvent("kural_answer_wrong", { kural_id, word_attempted, correct_word, attempt_count });

/** #36 — User taps "Type the Kural" button */
export const trackTypeKuralTap = ({ kural_id, words_already_scanned }) =>
  gaEvent("type_kural_tap", { kural_id, words_already_scanned });

/** #37 — "Congrats! You completed your Kural!" */
export const trackKuralComplete = ({ kural_id, time_to_complete_sec, words_scanned, words_typed, attempts_total }) =>
  gaEvent("kural_complete", { kural_id, time_to_complete_sec, words_scanned, words_typed, attempts_total });

// ─── VIDEO RECORDING & SUBMISSION ────────────────────────────────────────────

/** #38 — "Teach your child" screen appears */
export const trackVideoPromptShown = ({ kural_id }) =>
  gaEvent("video_prompt_shown", { kural_id });

/** #39 — User taps record */
export const trackVideoRecordStart = ({ kural_id }) =>
  gaEvent("video_record_start", { kural_id });

/** #40 — User stops recording */
export const trackVideoRecordStop = ({ kural_id, duration_sec }) =>
  gaEvent("video_record_stop", { kural_id, duration_sec });

/** #41 — User taps "Review" */
export const trackVideoReviewTap = ({ kural_id }) =>
  gaEvent("video_review_tap", { kural_id });

/** #42 — User taps "Submit" */
export const trackVideoSubmit = ({ kural_id, duration_sec }) =>
  gaEvent("video_submit", { kural_id, duration_sec });

/** #43 — "Already submitted this week" shown */
export const trackVideoAlreadySubmittedError = ({ kural_id, days_since_last_submit }) =>
  gaEvent("video_already_submitted_error", { kural_id, days_since_last_submit });

// ─── REWARDS & PRIZES ────────────────────────────────────────────────────────

/** #44 — "Stay Tuned!" screen with prizes */
export const trackPrizeTeaserShown = ({ prize_type_shown }) =>
  gaEvent("prize_teaser_shown", { prize_type_shown });

/** #45 — "Your entry is successful" badge */
export const trackEntryConfirmationShown = ({ entry_type }) =>
  gaEvent("entry_confirmation_shown", { entry_type });

// ─── NAVIGATION & HELP ───────────────────────────────────────────────────────

/** #46 — User taps "Get Help" */
export const trackGetHelpTap = ({ current_screen }) =>
  gaEvent("get_help_tap", { current_screen });

/** #47 — User taps home icon */
export const trackHomeTap = ({ from_screen, kural_in_progress }) =>
  gaEvent("home_tap", { from_screen, kural_in_progress });

/** #48 — User taps Instagram/X/Facebook */
export const trackSocialLinkTap = ({ platform, current_screen }) =>
  gaEvent("social_link_tap", { platform, current_screen });

/** #49 — User switches TM↔EN mid-session */
export const trackLanguageToggle = ({ from_lang, to_lang, current_screen }) =>
  gaEvent("language_toggle", { from_lang, to_lang, current_screen });

/** #50 — User taps "Scan Biscuit" from home */
export const trackScanBiscuitCtaTap = ({ kural_id, words_remaining }) =>
  gaEvent("scan_biscuit_cta_tap", { kural_id, words_remaining });

// ─── ADDITIONAL EVENTS (screen views, dashboard, etc.) ───────────────────────

/** Dashboard screen view with kural + cashback context */
export const trackDashboardView = ({ kurals_unlocked, kurals_completed, total_cashback_earned }) =>
  gaEvent("dashboard_view", { kurals_unlocked, kurals_completed, total_cashback_earned });

/** User submits a kural word in type-kural mode */
export const trackKuralWordSubmit = ({ kural_id, word_position, is_correct }) =>
  gaEvent("kural_word_submit", { kural_id, word_position, is_correct });

/** Alias: kural_unlocked — fired when new kural words arrive after biscuit scan */
export const trackKuralUnlocked = ({ kural_id, kural_chapter, cashback_amount }) =>
  gaEvent("kural_unlocked", { kural_id, kural_chapter, cashback_amount });

/** Alias for biscuit scan failure (broader params) */
export const trackBiscuitScanFailure = ({ kural_id, word_position, failure_reason, attempt_count } = {}) =>
  gaEvent("biscuit_scan_fail", { kural_id, word_position, attempt_count, failure_reason });

/** Video screen (ShareVideo) loaded */
export const trackVideoScreenView = () => gaEvent("video_screen_view", {});

/** Alias: user starts recording */
export const trackVideoRecordingStart = (params = {}) => trackVideoRecordStart(params);

/** Alias: user stops recording */
export const trackVideoRecordingStop = ({ duration_sec } = {}) =>
  gaEvent("video_record_stop", { duration_sec });

/** Video submission failed */
export const trackVideoSubmitFail = ({ failure_reason }) =>
  gaEvent("video_submit_fail", { failure_reason });

/** Rewards screen loaded */
export const trackRewardScreenView = () => gaEvent("reward_screen_view", {});

/** Menu screen/drawer opened */
export const trackMenuOpen = () => gaEvent("menu_open", {});

/** Buy more clicked */
export const trackBuyMoreClick = () => gaEvent("buy_more_click", {});
